import { useTranslation } from 'react-i18next'
import LoadingIndicator from '@/widgets/LoadingIndicator'
import NoResultsIndicator from '@/widgets/NoResultsIndicator'
import { getFilterIndicator, getSortIndicator } from '@/lib/tableHelpers'
import TrainingsTableRow from './TrainingsTableRow'

export default function TrainingsTable({
	trainingsData,
	loading,
	sortColumn,
	sortDirection,
	onSort,
	onDelete,
	hasPlacowkaFilter,
	onOpenPlacowkaFilter,
	hasTrenerFilter,
	onOpenTrenerFilter,
	hasStatusFilter,
	onOpenStatusFilter,
}) {
	const { t } = useTranslation()
	const rows = Array.isArray(trainingsData) ? trainingsData : []

	const renderColumnHeader = ({ key, label, sortable = true, hasFilter = false, onOpenFilter, className = '' }) => {
		const columnLabel = sortable ? (
			<button
				type="button"
				className="flex items-center gap-1 font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
				onClick={() => onSort(key)}
			>
				{label}
				{getSortIndicator(key, sortColumn, sortDirection)}
			</button>
		) : (
			<span className="font-semibold text-gray-700">{label}</span>
		)

		const content = onOpenFilter ? (
			<div className="inline-flex items-center gap-1">
				{columnLabel}
				<button
					type="button"
					className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
					onClick={onOpenFilter}
					title={t('trainings.list.table.filterColumn')}
				>
					{getFilterIndicator(hasFilter)}
				</button>
			</div>
		) : columnLabel

		return <th className={`py-2 px-4 border-b border-gray-300 text-left ${className}`.trim()}>{content}</th>
	}

	if (loading) return <LoadingIndicator />

	return (
		<div className="mt-6 overflow-x-auto">
			<table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
				<thead>
					<tr>
						{renderColumnHeader({
							key: 'trainingName',
							label: t('trainings.list.table.trainingName'),
						})}
						{renderColumnHeader({
							key: 'placowkaName',
							label: t('trainings.list.table.placowkaName'),
							hasFilter: hasPlacowkaFilter,
							onOpenFilter: onOpenPlacowkaFilter,
							className: 'w-[30%] max-w-[280px]',
						})}
						{renderColumnHeader({
							key: 'trainerName',
							label: t('trainings.list.table.trainerName'),
							hasFilter: hasTrenerFilter,
							onOpenFilter: onOpenTrenerFilter,
						})}
						{renderColumnHeader({
							key: 'trainingDate',
							label: t('trainings.list.table.trainingDate'),
						})}
						{renderColumnHeader({
							key: 'status',
							label: t('trainings.list.table.status'),
							hasFilter: hasStatusFilter,
							onOpenFilter: onOpenStatusFilter,
						})}
						<th className="py-2 px-4 border-b border-gray-300 text-right w-24">
							<span className="font-semibold text-gray-700">{t('trainings.list.table.actions')}</span>
						</th>
					</tr>
				</thead>
				<tbody>
					{rows.map(training => (
						<TrainingsTableRow
							key={training._id}
							training={training}
							onDelete={onDelete}
						/>
					))}
				</tbody>
			</table>
			{rows.length === 0 ? (
				<div className="py-4">
					<NoResultsIndicator
						message={t('trainings.list.emptyState.title')}
						description={t('trainings.list.emptyState.description')}
					/>
				</div>
			) : null}
		</div>
	)
}
